import { useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import SectionHeader from "./SectionHeader.jsx";
import { resolveAssetUrl } from "../../utils/resolveAssetUrl.js";

const formatDate = (value) => {
  if (!value) return "Blog";
  const raw = String(value);
  if (!raw.trim()) return "Blog";
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export default function JournalGrid({ items = [] }) {
  const railRef = useRef(null);
  const displayItems = useMemo(() => {
    if (Array.isArray(items) && items.length) return items;
    return Array.from({ length: 4 }).map((_, idx) => ({ id: `skeleton-${idx}` }));
  }, [items]);

  const scrollByViewport = (direction) => {
    const rail = railRef.current;
    if (!rail) return;
    rail.scrollBy({
      left: direction * rail.clientWidth,
      behavior: "smooth",
    });
  };

  const action = (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => scrollByViewport(-1)}
        className="grid h-10 w-10 place-items-center rounded-full border border-[var(--border)] bg-white text-[var(--ink)] shadow-soft-sm transition hover:border-[var(--gold)] hover:text-[var(--gold)]"
        aria-label="Scroll blogs left"
      >
        {"\u2039"}
      </button>
      <Link
        to="/blog"
        onClick={() => scrollByViewport(1)}
        className="flex items-center gap-2 rounded-full border border-[rgba(201,161,91,0.35)] bg-white px-4 py-2 text-sm font-medium text-[var(--ink)] shadow-soft-sm transition hover:border-[var(--gold)] hover:text-[var(--gold)]"
      >
        View more
        <span className="text-lg leading-none">{"\u203A"}</span>
      </Link>
    </div>
  );

  return (
    <section id="journal" className="bg-[var(--surface)] py-10 md:py-12">
      <div className="landing-container">
        <SectionHeader
          eyebrow="Blogs"
          title="Latest from the Homevilas blog"
          subtitle="Travel inspiration, villa guides, and slow-living itineraries curated by our team."
          action={action}
        />
        <div
          ref={railRef}
          className="scrollbar-hidden mt-4 grid grid-flow-col auto-cols-[80%] gap-3 overflow-x-auto pb-1 pt-1 snap-x snap-mandatory sm:auto-cols-[calc((100%-12px)/2)] lg:auto-cols-[calc((100%-36px)/4)]"
        >
          {displayItems.map((article) => {
            if (!article.title) {
              return (
                <div
                  key={article.id}
                  className="snap-start overflow-hidden rounded-3xl border border-[var(--border)] bg-white shadow-soft-sm"
                >
                <div className="h-32 w-full bg-[var(--surface-strong)]" />
                  <div className="p-3 space-y-3">
                    <div className="h-4 w-24 rounded-full bg-[var(--surface-strong)]" />
                    <div className="h-4 w-3/4 rounded-full bg-[var(--surface-strong)]" />
                    <div className="h-3 w-full rounded-full bg-[var(--surface-strong)]" />
                    <div className="h-3 w-5/6 rounded-full bg-[var(--surface-strong)]" />
                  </div>
                </div>
              );
            }

            const imageUrl = resolveAssetUrl(article.image || article.image_url || "");
            const dateText = article.date || formatDate(article.published_at || article.created_at);
            const excerpt =
              article.excerpt ||
              (article.body ? `${String(article.body).slice(0, 140)}...` : "Read more from the Homevilas blog.");
            const link = article.link || (article.slug ? `/blog/${article.slug}` : "/blog");

            return (
              <Link
                key={article.id}
                to={link}
                className="group snap-start overflow-hidden rounded-3xl border border-[var(--border)] bg-white shadow-soft-sm transition hover:-translate-y-1 hover:shadow-soft-lg"
              >
                <div className="relative h-32 w-full bg-[var(--surface-strong)]">
                  {imageUrl ? (
                    <img src={imageUrl} alt={article.title} className="h-full w-full object-cover" loading="lazy" />
                  ) : (
                    <div className="flex h-full items-center justify-center text-xs text-[var(--muted)]">No image</div>
                  )}
                  <div className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-[10px] font-medium uppercase tracking-[0.2em] text-[var(--text-muted)]">
                    {dateText}
                  </div>
                </div>
                <div className="p-3">
                  <h3 className="text-base font-semibold text-[var(--ink)]">{article.title}</h3>
                  <p className="mt-2 text-sm text-[var(--text-muted)] line-clamp-2">{excerpt}</p>
                  <div className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-[var(--gold)]">
                    Read more
                    <span className="text-base">{"\u2192"}</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
